import React from 'react';

import { contactApis } from '@apis/next/contactApis';
import {
  ManagementFormContainer,
  ManagementFormContextValuesInput,
  ManagementFormLabel,
  ManagementFormInput,
} from '@components/NextForms/ManagementForm';
import { contactStrings } from '@data/contacts';
import { useCNZipCodeAutoComplete, useJPZipCodeAutoFill } from '@hooks/next/useZipCodeAutoComplete';
import { useIsAdmin } from '@hooks/useIsAdmin';
import { BaseContactForm, ContactFormItem, ContactType } from '@models/next/Contact';
import { MobxZodField, MobxZodObjectField } from '@monoid-dev/mobx-zod-form';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import { useDebounce } from 'react-use';
import { ZodTypeAny } from 'zod';

const strings = createStrings({
  ja: {
    placeholdersCN: {
      company_name: '例：东方物产有限公司',
      english_name: '例：Dongfang Wuchan Co., Ltd.',
      zip_code: '6桁の中国の郵便番号を入力してください　例：523000',
      address1_cn: '都市名（中国語）',
      address1: '都市名（英字名）',
      address2: '中国の街道・建物名・号室などを入力してください　例：No.○, ○○ Road',
      principal_name: '例：南さん',
      phone: '例：138 xxxx xxxx',
      email: '例：d2dtaro@d2dmail.com',
      sns_id: '例：wechat ID: d2dtaro',
    },
    placeholdersJP: {
      company_name: '例：株式会社　ABC',
      english_name: '例：ABC Co., Ltd.',
      zip_code: '日本の郵便番号を入力してください',
      address1_cn: '都市名（中国語）',
      address1: '都道府県・市区町村・番地を入力してください',
      address2: '建物名・号室を入力してください',
      principal_name: '例：荷物太郎',
      phone: '例：03-1234-2222',
      email: '例：d2dtaro@d2dmail.com',
      sns_id: '例：wechat ID: d2dtaro',
    },
    cnAddressInputTips:
      '＊中国国内の都市名(英字名)と郵便番号のいずれか一つを入力して、現れた選択肢中から一つを選んでください',
    usciTips:
      '＊統一社会信用コード(Unified Social Credit Identifier：USCI)は中国の各地域の工商局で登記された零細会社を含む法人の唯一不変の18桁からなる組織コード番号で統一企業コードとも呼ばれています。',
    pleaseInputValidZipCode: '有効な郵便番号を入力してください',
    pleaseInputValidCity: '有効な都市名を入力してください',
  },
});

const useStyles = makeMuiStyles(() => ({
  cnAddressInputContainer: {
    display: 'grid',
    columnGap: '1rem',
    rowGap: '0.5rem',
    gridTemplateColumns: '1fr 1fr 1fr',
    '&$omitAddress1CN': {
      gridTemplateColumns: '2fr 1fr',
    },
  },
  socialCreditIdContainer: {
    display: 'grid',
    columnGap: '1rem',
    rowGap: '0.5rem',
  },
  omitAddress1CN: {},
}));

export interface HandleUsciValidationProps {
  id: number;
  socialCreditIdField: MobxZodField<ZodTypeAny>;
}

const HandleUsciValidation: React.FC<HandleUsciValidationProps> = observer(
  ({ id, socialCreditIdField }) => {
    const shipperContactsQuery = contactApis.useGet('/contact', {
      queries: {
        contact_type: 'shipper',
      },
    });

    useDebounce(
      () => {
        requestIdleCallback(() => {
          const decodeResult = socialCreditIdField.decodeResult;
          if (decodeResult.success) {
            if (
              shipperContactsQuery.data?.find(
                (contact) => contact.social_credit_id === decodeResult.data && contact.id !== id,
              )
            ) {
              socialCreditIdField.setExtraErrorMessages([contactStrings.usciDuplicatedError]);
            } else {
              socialCreditIdField.setExtraErrorMessages([]);
            }
          }
        });
      },
      500,
      [socialCreditIdField.decodeResult],
    );
    return null;
  },
);

export interface ContactInputProps extends ManagementFormContextValuesInput {
  /**
   * Node to insert before all form children
   */
  firstChild?: React.ReactNode;
  field: MobxZodObjectField<typeof BaseContactForm>;
  type: ContactType;
  item: ContactFormItem;
}

export const ContactInput: React.FC<ContactInputProps> = observer(
  ({ firstChild, field, type, item, ...contextValues }) => {
    const { fields } = field;
    const { mode } = contextValues;

    const classes = {
      ...useCommonStyles(),
      ...useStyles(),
    };

    const isAdmin = useIsAdmin();

    const showSocialCreditId = type === 'shipper';

    const isJP = type === 'consignee' || type === 'receiver';

    if (isJP) {
      useJPZipCodeAutoFill({
        zipcodeField: fields.zip_code,
        address1Field: fields.address1,
      });
    }

    const cnAutoCompleteOptions = !isJP
      ? useCNZipCodeAutoComplete({
          address1CNField: fields.address1_cn,
          address1Field: fields.address1,
          zipcodeField: fields.zip_code,
        })
      : undefined;

    const placeholders = isJP ? strings.placeholdersJP : strings.placeholdersCN;

    return (
      <ManagementFormContainer {...contextValues} containerProps={{ 'data-testid': 'ContactForm' }}>
        {firstChild}

        <ManagementFormLabel field={fields.company_name} required={isJP} />
        <ManagementFormInput
          field={fields.company_name}
          inputProps={{ placeholder: placeholders.company_name }}
        />

        <ManagementFormLabel field={fields.english_name} required />
        <ManagementFormInput
          field={fields.english_name}
          inputProps={{ placeholder: placeholders.english_name }}
        />

        {showSocialCreditId && (
          <>
            <HandleUsciValidation id={item.id} socialCreditIdField={fields.social_credit_id} />
            <ManagementFormLabel field={fields.social_credit_id} />
            <div className={classes.socialCreditIdContainer}>
              <ManagementFormInput
                field={fields.social_credit_id}
                inputProps={{ placeholder: 'XX-XXXXXX-XXXXXXXX-X' }}
                popperConfig={{
                  width: 'max-content',
                }}
              />
              {mode === 'edit' && (
                <div
                  style={{ gridColumn: '1 / -1' }}
                  className={clsx(classes.exSmallerText, classes.textGray500)}
                >
                  {strings.usciTips}
                </div>
              )}
            </div>
          </>
        )}

        {isJP && (
          <>
            <ManagementFormLabel field={fields.zip_code} required />
            <ManagementFormInput
              field={fields.zip_code}
              inputProps={{ placeholder: placeholders.zip_code }}
            />

            <ManagementFormLabel field={fields.address1} required />
            <ManagementFormInput
              field={fields.address1}
              inputProps={{ placeholder: placeholders.address1 }}
            />
          </>
        )}

        {!isJP && (
          <>
            {mode === 'edit' && (
              <>
                <ManagementFormLabel field={fields.address1} required />
                <div
                  className={clsx(
                    classes.cnAddressInputContainer,
                    !isAdmin && classes.omitAddress1CN,
                  )}
                >
                  {isAdmin && (
                    <ManagementFormInput
                      field={fields.address1_cn}
                      inputProps={{ placeholder: placeholders.address1_cn }}
                      autoCompleteConfig={cnAutoCompleteOptions!.address1_cn}
                      popperConfig={{
                        width: 'max-content',
                      }}
                    />
                  )}

                  <ManagementFormInput
                    field={fields.address1}
                    inputProps={{ placeholder: placeholders.address1 }}
                    autoCompleteConfig={cnAutoCompleteOptions!.address1}
                    popperConfig={{
                      width: 'max-content',
                    }}
                  />

                  <ManagementFormInput
                    field={fields.zip_code}
                    inputProps={{ placeholder: placeholders.zip_code }}
                    autoCompleteConfig={cnAutoCompleteOptions!.zipcode}
                    popperConfig={{
                      width: 'max-content',
                    }}
                  />

                  <div
                    style={{ gridColumn: '1 / -1' }}
                    className={clsx(classes.exSmallerText, classes.textGray500)}
                  >
                    {strings.cnAddressInputTips}
                  </div>
                </div>
              </>
            )}

            {mode === 'display' && (
              <>
                <ManagementFormLabel field={fields.zip_code} required />
                <ManagementFormInput
                  field={fields.zip_code}
                  inputProps={{ placeholder: placeholders.zip_code }}
                />

                <ManagementFormLabel field={fields.address1} required />
                <ManagementFormInput
                  field={fields.address1}
                  inputProps={{ placeholder: placeholders.address1 }}
                />
              </>
            )}
          </>
        )}

        <ManagementFormLabel field={fields.address2} />
        <ManagementFormInput
          field={fields.address2}
          inputProps={{ placeholder: placeholders.address2 }}
        />

        <ManagementFormLabel field={fields.principal_name} required />
        <ManagementFormInput
          field={fields.principal_name}
          inputProps={{ placeholder: placeholders.principal_name }}
        />

        <ManagementFormLabel field={fields.phone} required />
        <ManagementFormInput
          field={fields.phone}
          inputProps={{ placeholder: placeholders.phone }}
        />

        <ManagementFormLabel field={fields.email} />
        <ManagementFormInput
          field={fields.email}
          inputProps={{ placeholder: placeholders.email }}
        />

        <ManagementFormLabel field={fields.sns_id} />
        <ManagementFormInput
          field={fields.sns_id}
          inputProps={{ placeholder: placeholders.sns_id }}
        />
      </ManagementFormContainer>
    );
  },
);
